import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { Loader2, Calendar, MapPin, Clock, Search, Gamepad2 } from 'lucide-react';
import { SponsorBanner } from '../../components/ui';
import { isDemoTournament, getDemoTournament, getDemoTeams, getDemoMatches, getDemoPoules } from '../../data/demoData';

const PublicMatchesPage = () => {
    const { t } = useTranslation();
    const { id: tournamentId } = useParams();
    const outletContext = useOutletContext();
    const isEsports = outletContext?.isEsports || false;

    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [poules, setPoules] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedPoule, setSelectedPoule] = useState('all');
    const [selectedTeam, setSelectedTeam] = useState(() => localStorage.getItem('propoule_fav_team') || 'all');
    const [sponsorImages, setSponsorImages] = useState([]);

    useEffect(() => {
        fetchData();

        const subscription = supabase
            .channel('public_matches')
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'matches',
                filter: `tournament_id=eq.${tournamentId}`
            }, () => {
                fetchData(false);
            })
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [tournamentId]);

    // Save selection to local storage
    useEffect(() => {
        if (selectedTeam) {
            localStorage.setItem('propoule_fav_team', selectedTeam);
        }
    }, [selectedTeam]);

    const fetchData = async (showLoading = true) => {
        if (showLoading) setLoading(true);

        if (isDemoTournament(tournamentId)) {
            // Demo Mode
            const dTourney = getDemoTournament(tournamentId);
            setSponsorImages([dTourney.sponsor_left_image, dTourney.sponsor_right_image].filter(Boolean));
            setPoules(getDemoPoules(tournamentId));
            setTeams(getDemoTeams(tournamentId));
            setMatches(getDemoMatches(tournamentId));
            setLoading(false);
            return;
        }

        try {
            // Tournament info for sponsors
            const { data: tournamentData } = await supabase
                .from('tournaments')
                .select('sponsor_left_image, sponsor_right_image')
                .eq('id', tournamentId)
                .single();

            if (tournamentData) {
                const sponsors = [];
                if (tournamentData.sponsor_left_image) sponsors.push(...tournamentData.sponsor_left_image.split(','));
                if (tournamentData.sponsor_right_image) sponsors.push(...tournamentData.sponsor_right_image.split(','));
                setSponsorImages(sponsors.filter(Boolean));
            }

            // Get Poules
            const { data: poulesData } = await supabase
                .from('poules')
                .select('id, name')
                .eq('tournament_id', tournamentId)
                .order('name');
            if (poulesData) setPoules(poulesData);

            // Get Teams (for filter)
            const { data: teamsData } = await supabase
                .from('teams')
                .select('id, name')
                .eq('tournament_id', tournamentId)
                .order('name');
            if (teamsData) setTeams(teamsData);

            // Get Matches
            const { data: matchesData, error } = await supabase
                .from('matches')
                .select(`
                    id, poule_id, match_date, start_time, score_home, score_away, status, field, set_scores,
                    home_team: teams!matches_home_team_id_fkey(name, logo_url),
                    away_team: teams!matches_away_team_id_fkey(name, logo_url),
                    poule: poules(name),
                    home_team_id,
                    away_team_id
                `)
                .eq('tournament_id', tournamentId)
                .order('match_date', { ascending: true })
                .order('start_time', { ascending: true });

            if (error) throw error;
            setMatches(matchesData || []);
        } catch (err) {
            console.error('Error fetching matches:', err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    // Filter Logic
    let filteredMatches = matches;

    if (selectedTeam !== 'all') {
        // If a team is selected, show ONLY matches for that team (ignore poule filter)
        filteredMatches = matches.filter(m =>
            m.home_team_id === selectedTeam || m.away_team_id === selectedTeam
        );
    } else if (selectedPoule !== 'all') {
        // Otherwise filter by poule
        filteredMatches = matches.filter(m => m.poule_id === selectedPoule);
    }

    // Grouping by Date
    const matchesByDate = filteredMatches.reduce((acc, match) => {
        let date = match.match_date;
        if (!date && match.start_time && match.start_time.includes('T')) {
            date = match.start_time.split('T')[0];
        }
        if (!date) date = 'Onbekende datum';
        if (!acc[date]) acc[date] = [];
        acc[date].push(match);
        return acc;
    }, {});

    if (loading) return (
        <div className="flex justify-center p-8">
            <Loader2 className="animate-spin text-white" />
        </div>
    );

    return (
        <div className="p-4 space-y-4 pb-24 max-w-3xl mx-auto font-sans">
            {/* Team Filter (Top) */}
            <div className={`relative sticky top-0 z-30 pt-2 transition-colors ${isEsports ? 'bg-[#08080f]' : 'bg-white'}`}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className={isEsports ? 'text-purple-400' : 'text-gray-400'} />
                </div>
                <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className={`block w-full pl-10 pr-4 py-3 rounded-xl shadow-lg focus:ring-2 focus:ring-purple-500 appearance-none font-semibold text-sm transition-all ${
                        isEsports 
                            ? 'bg-[#0b0b14]/95 text-white border border-purple-500/20' 
                            : 'bg-white/95 text-gray-900 border-none'
                    }`}
                >
                    <option value="all" className={isEsports ? 'bg-[#0b0b14] text-white' : ''}>🔍 {t('public.search_placeholder')}</option>
                    {teams.map(team => (
                        <option key={team.id} value={team.id} className={isEsports ? 'bg-[#0b0b14] text-white' : ''}>{team.name}</option>
                    ))}
                </select>
            </div>

            {/* Poule Filter */}
            {selectedTeam === 'all' && (
                <div className={`flex gap-2 overflow-x-auto pb-2 scrollbar-hide py-2 -mx-4 px-4 backdrop-blur-sm transition-colors ${
                    isEsports ? 'bg-purple-950/15' : 'bg-black/10'
                }`}>
                    <button
                        onClick={() => setSelectedPoule('all')}
                        className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-all whitespace-nowrap shadow-sm ${
                            selectedPoule === 'all'
                                ? (isEsports 
                                    ? 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white border-purple-400/30 shadow-[0_0_10px_rgba(168,85,247,0.35)]' 
                                    : 'bg-gray-900 text-white border-gray-900')
                                : (isEsports 
                                    ? 'bg-purple-950/20 text-purple-300 border-purple-500/20 hover:bg-purple-500/10' 
                                    : 'bg-white/90 text-gray-800 border-white/20 hover:bg-white')
                        }`}
                    >
                        {t('public.filter_all_poules')}
                    </button>
                    {poules.map(poule => (
                        <button
                            key={poule.id}
                            onClick={() => setSelectedPoule(poule.id)}
                            className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-all whitespace-nowrap shadow-sm ${
                                selectedPoule === poule.id
                                    ? (isEsports 
                                        ? 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white border-purple-400/30 shadow-[0_0_10px_rgba(168,85,247,0.35)]' 
                                        : 'bg-gray-900 text-white border-gray-900')
                                    : (isEsports 
                                        ? 'bg-purple-950/20 text-purple-300 border-purple-500/20 hover:bg-purple-500/10' 
                                        : 'bg-white/90 text-gray-800 border-white/20 hover:bg-white')
                            }`}
                        >
                            {poule.name}
                        </button>
                    ))}
                </div>
            )}

            {selectedTeam !== 'all' && (
                <div className={`text-xs font-bold text-center px-4 py-1 rounded-full mx-auto w-fit backdrop-blur-sm animate-fade-in mb-2 ${
                    isEsports ? 'bg-purple-600/40 text-purple-200 border border-purple-500/30' : 'bg-indigo-600/80 text-white'
                }`}>
                    {t('public.filter_active')}: {teams.find(t => t.id === selectedTeam)?.name}
                    <button onClick={() => setSelectedTeam('all')} className="ml-2 underline text-white/85 hover:text-white">{t('public.clear')}</button>
                </div>
            )}

            {/* Matches List */}
            {Object.keys(matchesByDate).length === 0 ? (
                <div className={`text-center py-12 rounded border shadow-lg flex flex-col items-center justify-center gap-3 transition-colors ${
                    isEsports 
                        ? 'bg-[#0a0a14]/90 border-purple-500/20 text-slate-400 shadow-[0_0_20px_rgba(168,85,247,0.05)]' 
                        : 'bg-white/90 border-white/20 text-gray-500'
                }`}>
                    <Gamepad2 size={40} className={isEsports ? 'text-purple-400/50 animate-pulse' : 'text-gray-300'} />
                    <span className="font-bold text-sm tracking-wide">{t('matches_page.no_matches_title')}</span>
                </div>
            ) : (
                Object.entries(matchesByDate).map(([date, dateMatches]) => (
                    <div key={date} className="space-y-2">
                        <div className={`inline-block px-3 py-1.5 rounded text-xs font-mono font-bold uppercase tracking-wider shadow-md ${
                            isEsports 
                                ? 'bg-cyan-950/40 border border-cyan-500/20 text-cyan-300' 
                                : 'bg-gray-900 text-white'
                        }`}>
                            {date !== 'Onbekende datum'
                                ? new Date(date).toLocaleDateString(t('public.date_locale'), { weekday: 'long', day: 'numeric', month: 'long' })
                                : date
                            }
                        </div>
                        <div className={`rounded-xl border shadow-lg overflow-hidden divide-y transition-colors ${
                            isEsports 
                                ? 'bg-[#0a0a14]/90 border-purple-500/20 divide-purple-500/10' 
                                : 'bg-white/90 border-white/20 divide-gray-100'
                        }`}>
                            {dateMatches.map(match => (
                                <MatchItem key={match.id} match={match} highlightTeamId={selectedTeam} isEsports={isEsports} />
                            ))}
                        </div>
                    </div>
                ))
            )}

            {/* Sponsor Banner */}
            {sponsorImages.length > 0 && (
                <div className="mt-8 animate-fade-in">
                    <SponsorBanner sponsorUrls={sponsorImages} />
                </div>
            )}
        </div>
    );
};

const MatchItem = ({ match, highlightTeamId, isEsports }) => {
    const hasScore = match.score_home !== null && match.score_away !== null;
    
    const formatTime = (timeStr) => {
        if (!timeStr) return '--:--';
        if (timeStr.includes('T')) {
            try {
                const dateObj = new Date(timeStr);
                return dateObj.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
            } catch (e) {
                return '--:--';
            }
        }
        return timeStr.slice(0, 5);
    };

    const time = formatTime(match.start_time);

    // Highlight logic
    const isHome = match.home_team_id === highlightTeamId;
    const isAway = match.away_team_id === highlightTeamId;

    return (
        <div className={`p-3.5 transition-all border-b relative ${
            isEsports 
                ? (isHome || isAway 
                    ? 'bg-purple-900/15 hover:bg-purple-900/25 border-l-4 border-l-purple-500 border-b-purple-500/10' 
                    : 'hover:bg-purple-500/5 border-purple-500/10')
                : (isHome || isAway 
                    ? 'bg-indigo-50/80 border-b-gray-50/50' 
                    : 'hover:bg-indigo-50/30 border-b-gray-50/50')
        }`}>
            <div className="flex items-center justify-between">
                {/* Left: Time & Poule */}
                <div className="w-16 shrink-0 text-xs flex flex-col gap-1 items-start font-mono">
                    {match.status === 'live' || match.status === 'in_progress' ? (
                        <span className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shadow-[0_0_8px_rgba(6,182,212,0.35)] animate-pulse ${
                            isEsports 
                                ? 'text-cyan-300 bg-cyan-950/40 border border-cyan-500/30 shadow-[0_0_8px_rgba(6,182,212,0.2)]' 
                                : 'text-red-600 bg-red-50 border border-red-200'
                        }`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${isEsports ? 'bg-cyan-400 animate-ping' : 'bg-red-500 animate-ping'}`}></span>
                            LIVE
                        </span>
                    ) : (
                        <span className={`font-bold flex items-center gap-1 ${isEsports ? 'text-cyan-400' : 'text-gray-800'}`}>
                            <Clock size={10} className={isEsports ? 'text-cyan-400' : ''} /> {time}
                        </span>
                    )}
                    <span className={`truncate max-w-full text-[9px] font-black uppercase tracking-wider ${isEsports ? 'text-purple-400' : 'text-gray-400'}`}>
                        {match.poule?.name?.replace('Poule ', '')}
                    </span>
                    {match.field && (
                        <span className={`flex items-center gap-1 text-[9px] px-1 rounded ${isEsports ? 'text-slate-400 bg-purple-950/30 border border-purple-500/10' : 'text-gray-400 bg-gray-100'}`}>
                            <MapPin size={8} /> {match.field}
                        </span>
                    )}
                </div>

                {/* Middle: Teams & Score */}
                <div className="flex-1 flex items-center justify-between gap-1 mx-2 overflow-hidden">
                    {/* Home Team */}
                    <div className="flex-1 flex items-center justify-end gap-2 overflow-hidden">
                        <div className={`text-xs sm:text-sm leading-tight text-right break-words ${
                            isEsports 
                                ? (isHome 
                                    ? 'font-black text-purple-300 drop-shadow-[0_0_5px_rgba(168,85,247,0.4)]' 
                                    : (hasScore && match.score_home > match.score_away ? 'font-black text-white' : 'font-medium text-slate-300'))
                                : (isHome 
                                    ? 'font-black text-indigo-700' 
                                    : (hasScore && match.score_home > match.score_away ? 'font-black text-gray-900' : 'font-medium text-gray-700'))
                        }`}>
                            {match.home_team?.name || '...'}
                        </div>
                        {match.home_team?.logo_url && (
                            <img 
                                src={match.home_team.logo_url} 
                                className={`w-7 h-7 rounded object-contain shrink-0 p-0.5 ${isEsports ? 'bg-[#12121e] border border-purple-500/20 shadow-inner' : 'bg-white border border-gray-100'}`} 
                                alt=""
                            />
                        )}
                    </div>

                    {/* Score Box */}
                    <div className="shrink-0 w-16 flex justify-center">
                        {hasScore ? (
                            isEsports ? (
                                <div className={`text-white font-mono font-black px-2.5 py-1 rounded text-sm shadow-[0_0_10px_rgba(0,0,0,0.5)] min-w-[2.8rem] text-center leading-none flex flex-col items-center justify-center border ${
                                    (isHome && match.score_home > match.score_away) || (isAway && match.score_away > match.score_home) 
                                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 border-green-400/30 shadow-[0_0_10px_rgba(16,185,129,0.3)]' 
                                        : 'bg-purple-950/80 border-purple-500/30 text-slate-100'
                                }`}>
                                    <span>{match.score_home}-{match.score_away}</span>
                                </div>
                            ) : (
                                <div className={`text-white font-black px-1.5 py-1 rounded-lg text-sm shadow-sm min-w-[2.5rem] text-center leading-none flex flex-col items-center justify-center ${isHome && match.score_home > match.score_away ? 'bg-green-600' : (isAway && match.score_away > match.score_home ? 'bg-green-600' : 'bg-gray-900')}`}>
                                    <span>{match.score_home}-{match.score_away}</span>
                                </div>
                            )
                        ) : (
                            <span className={`text-[10px] font-black tracking-widest ${isEsports ? 'text-slate-500 font-mono' : 'text-gray-300'}`}>VS</span>
                        )}
                    </div>

                    {/* Away Team */}
                    <div className="flex-1 flex items-center justify-start gap-2 overflow-hidden">
                        {match.away_team?.logo_url && (
                            <img 
                                src={match.away_team.logo_url} 
                                className={`w-7 h-7 rounded object-contain shrink-0 p-0.5 ${isEsports ? 'bg-[#12121e] border border-purple-500/20 shadow-inner' : 'bg-white border border-gray-100'}`} 
                                alt=""
                            />
                        )}
                        <div className={`text-xs sm:text-sm leading-tight text-left break-words ${
                            isEsports 
                                ? (isAway 
                                    ? 'font-black text-purple-300 drop-shadow-[0_0_5px_rgba(168,85,247,0.4)]' 
                                    : (hasScore && match.score_away > match.score_home ? 'font-black text-white' : 'font-medium text-slate-300'))
                                : (isAway 
                                    ? 'font-black text-indigo-700' 
                                    : (hasScore && match.score_away > match.score_home ? 'font-black text-gray-900' : 'font-medium text-gray-700'))
                        }`}>
                            {match.away_team?.name || '...'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Set Scores */}
            {hasScore && match.set_scores && Array.isArray(match.set_scores) && match.set_scores.length > 0 && (
                <div className="flex justify-center gap-2 mt-2">
                    {match.set_scores.map((s, i) => (
                        <span key={i} className={`text-[10px] px-2 py-0.5 rounded font-mono border ${
                            isEsports 
                                ? 'bg-purple-950/20 text-purple-300 border-purple-500/20' 
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}>
                            {s.home}-{s.away}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PublicMatchesPage;
